import React, { useState } from 'react';
import clsx from 'clsx';

import { Review } from './Review';
import { useLocalize } from '../../hooks';
import { Button, DoughPieChart } from '..';
import { QuizResultStatus } from './QuizResultStatus';
import type { CompleteQuizResult } from '../../api';

type QuizResultProps = {
  goToCourse?: () => void;
  goToReview?: () => void;

  className?: string;
  result?: CompleteQuizResult;

  withReview?: boolean;
};

const QuizResultComponent: React.FC<QuizResultProps> = ({
  result,
  className,

  goToReview,
  goToCourse,
}) => {
  const { t } = useLocalize();
  if (!result) return;

  const { passed, correctAnswers = 0, totalQuestions = 1 } = result;
  const percent = (correctAnswers * 100) / (totalQuestions || 1);

  const graphic = [
    { name: t('course.page.wrong'), value: 100 - percent },
    { name: t('course.page.correct'), value: percent },
  ];

  return (
    <div className={clsx('flex flex-col items-center', className)}>
      <div className="flex flex-col px-[8rem] py-2xl items-center border border-solid border-accordionBorder rounded-bt shadow-[0px_4px_24px_0px_rgba(184,185,193,0.2)]">
        <h3 className="text-big font-bold">
          {t('course.page.quizresult')}:{' '}
          {passed ? t('course.page.success') : t('course.page.fail')}
        </h3>
        <div className="mt-md mb-lg w-[14.4rem] h-[14.4rem]">
          <DoughPieChart
            innerRadius={55}
            outerRadius={72}
            graphic={graphic}
            percent={percent}
            labelClassName={'text-section'}
          />
        </div>
        <QuizResultStatus result={result} />
        {(!!goToCourse || !!goToReview) && (
          <div className="w-full pt-lg bottom-0 flex items-center justify-center gap-x-md border-solid border-accordionBorder text-center">
            {!!goToCourse && (
              <Button
                type="empty"
                onClick={goToCourse}
                disabled={!goToCourse}
                label={t('course.page.backtocourse')}
                className={clsx(
                  'bg-main hover:bg-goToPracticeHover',
                  'border border-solid border-blackBorder bg-white text-blackBorder hover:bg-goToPracticeHover hover:text-white hover:border-transparent'
                )}
              />
            )}
            {!!goToReview && (
              <Button
                onClick={goToReview}
                label={t('quiz.page.details')}
                className="bg-main border-transparent hover:bg-goToPracticeHover hover:border-transparent"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const QuizResult: React.FC<QuizResultProps> = ({
  withReview,
  ...rest
}) => {
  const [showReview, setShowReview] = useState(false);

  if (!withReview) return <QuizResultComponent {...rest} />;

  if (!showReview)
    return (
      <QuizResultComponent {...rest} goToReview={() => setShowReview(true)} />
    );

  return <Review {...rest} goToSummary={() => setShowReview(false)} />;
};

export { QuizResultStatus } from './QuizResultStatus';
